import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import toast, { Toaster } from "react-hot-toast";
import Layout from "../layouts/Layout";
import {
  API_BASE_URL,
  FONT_HEADING,
  FONT_UI,
  SKIN_THEMES,
} from "../layouts/constants";

function useSkin() {
  const skinId = localStorage.getItem("skinTheme") || "navy";
  return SKIN_THEMES.find((s) => s.id === skinId) || SKIN_THEMES[0];
}

function formatValue(value, fallback = "") {
  return value === null || value === undefined || value === ""
    ? fallback
    : String(value);
}

function formatDateTime(value, fallback = "") {
  if (!value) return fallback;

  const raw = String(value);
  const match = raw.match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/);

  if (match) {
    const [, yyyy, mm, dd, hh, min] = match;
    return `${dd}-${mm}-${yyyy} ${hh}:${min}`;
  }

  return raw.replace("T", " ").slice(0, 16);
}

function toDateTimeLocal(value) {
  if (!value) return "";

  const raw = String(value);
  const match = raw.match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/);

  if (match) {
    const [, yyyy, mm, dd, hh, min] = match;
    return `${yyyy}-${mm}-${dd}T${hh}:${min}`;
  }

  return raw.replace(" ", "T").slice(0, 16);
}

export default function SentinelRouteAllocations() {
  const navigate = useNavigate();
  const location = useLocation();

  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);

  const routeId = Number(params.get("route_id") || 0);
  const initialRouteName = params.get("route_name") || "";
  const initialSearch = params.get("q") || "";
  const initialPage = Number(params.get("page") || 1);

  const recordsPerPage = 10;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const [dashboardData, setDashboardData] = useState({
    company_name: "",
    licensedFor: "",
    admin_active: 0,
    company_address: "",
    company_phone: "",
    admin_email: "",
    company_logo: "",
    user_map: "",
  });

  const [routeName, setRouteName] = useState(initialRouteName);
  const [searchInput, setSearchInput] = useState(initialSearch);
  const [search, setSearch] = useState(initialSearch);
  const [page, setPage] = useState(initialPage > 0 ? initialPage : 1);

  const [allocations, setAllocations] = useState([]);
  const [guards, setGuards] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editForm, setEditForm] = useState({
    allocation_id: "",
    route_id: "",
    guard_member_id: "",
    shift_label: "",
    start_time: "",
    end_time: "",
  });

  useEffect(() => {
    const token = localStorage.getItem("token") || localStorage.getItem("authToken");
    const isLoggedIn = localStorage.getItem("isLoggedIn");
    const userData = JSON.parse(localStorage.getItem("userData") || "{}");

    if (!token || !isLoggedIn) {
      navigate("/");
      return;
    }

    if (!routeId) {
      toast.error("Missing or invalid sentinel route ID.");
      setLoading(false);
      return;
    }

    const local = {
      company_name: localStorage.getItem("company_name") || userData.company_name || "",
      licensedFor: localStorage.getItem("licensedFor") || userData.licensedFor || "",
      admin_active: Number(localStorage.getItem("admin_active") ?? userData.admin_active ?? 0),
      company_address: localStorage.getItem("company_address") || userData.company_address || "",
      company_phone: localStorage.getItem("company_phone") || userData.company_phone || "",
      admin_email: localStorage.getItem("admin_email") || userData.admin_email || "",
      company_logo: localStorage.getItem("company_logo") || userData.company_logo || "",
      user_map: localStorage.getItem("user_map") || userData.user_map || "",
    };

    setDashboardData(local);
    fetchPageData(local, token, page, search);
    fetchGuards(token);
  }, [navigate]);

  useEffect(() => {
    const token = localStorage.getItem("token") || localStorage.getItem("authToken");
    if (!token || !routeId) return;

    fetchPageData(dashboardData, token, page, search);
  }, [page, search]);

  const fetchPageData = async (local, token, currentPage, currentSearch) => {
    try {
      setLoading(true);

      const [dashboardRes, allocationsRes] = await Promise.all([
        fetch(`${API_BASE_URL}/dashboard/admin/dashboard`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }),
        fetch(`${API_BASE_URL}/sentinel_allocate/${encodeURIComponent(routeId)}`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          cache: "no-store",
        }),
      ]);

      if (!dashboardRes.ok) throw new Error("Failed to fetch dashboard");
      if (!allocationsRes.ok) throw new Error("Failed to fetch allocations");

      const dashboardResult = await dashboardRes.json();
      const allocationsResult = await allocationsRes.json();

      const companyDetails = dashboardResult?.data?.companyDetails || {};

      setDashboardData({
        ...local,
        company_name: companyDetails.company_name ?? local.company_name ?? "",
        licensedFor: companyDetails.licensedFor ?? local.licensedFor ?? "",
        admin_active: Number(companyDetails.admin_active ?? local.admin_active ?? 0),
        company_address: companyDetails.company_address ?? local.company_address ?? "",
        company_phone: companyDetails.company_phone ?? local.company_phone ?? "",
        admin_email: companyDetails.admin_email ?? local.admin_email ?? "",
        company_logo: companyDetails.company_logo ?? local.company_logo ?? "",
        user_map: companyDetails.user_map ?? local.user_map ?? "",
      });

      const allocationRows = Array.isArray(allocationsResult?.data)
        ? allocationsResult.data
        : [];

      let filteredRows = allocationRows;

      if (currentSearch?.trim()) {
        const keyword = currentSearch.trim().toLowerCase();

        filteredRows = filteredRows.filter((row) => {
          const guardText = String(
            row.guard_label ||
              row.guard_name ||
              row.username ||
              row.fld_username ||
              row.guard_member_id ||
              ""
          ).toLowerCase();

          const shiftText = String(row.shift_label || "").toLowerCase();

          return guardText.includes(keyword) || shiftText.includes(keyword);
        });
      }

      const startIndex = (currentPage - 1) * recordsPerPage;
      const pagedRows = filteredRows.slice(startIndex, startIndex + recordsPerPage);

      setRouteName(initialRouteName || `Sentinel Route #${routeId}`);
      setAllocations(pagedRows);
      setTotalRecords(filteredRows.length);
      setTotalPages(Math.max(1, Math.ceil(filteredRows.length / recordsPerPage)));
    } catch (error) {
      console.error("Sentinel route allocations page error:", error);
      toast.error(error.message || "Failed to load sentinel allocations.");
      setAllocations([]);
      setTotalRecords(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  };

  const fetchGuards = async (tokenArg) => {
    try {
      const token =
        tokenArg || localStorage.getItem("token") || localStorage.getItem("authToken");

      const res = await fetch(`${API_BASE_URL}/users/`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        cache: "no-store",
      });

      const data = await res.json();

      if (!res.ok || data.success === false) {
        throw new Error(data.message || "Failed to fetch guards");
      }

      setGuards(Array.isArray(data.data) ? data.data : []);
    } catch (error) {
      console.error("Fetch guards error:", error);
      toast.error("Failed to load guards list.");
      setGuards([]);
    }
  };

  const updateUrl = (nextPage, nextSearch) => {
    const query = new URLSearchParams();

    query.set("route_id", String(routeId));

    if (routeName) query.set("route_name", routeName);
    if (nextSearch?.trim()) query.set("q", nextSearch.trim());
    if (nextPage > 1) query.set("page", String(nextPage));

    navigate(`/sentinel_allocates?${query.toString()}`);
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();

    const nextSearch = searchInput.trim();

    setPage(1);
    setSearch(nextSearch);
    updateUrl(1, nextSearch);
  };

  const clearSearch = () => {
    setSearchInput("");
    setSearch("");
    setPage(1);
    updateUrl(1, "");
    toast.success("Search cleared");
  };

  const handlePageChange = (nextPage) => {
    setPage(nextPage);
    updateUrl(nextPage, search);
  };

  const openRouteMap = (allocation) => {
    const url = `/sentinel_route_tracking?route_id=${encodeURIComponent(
      routeId
    )}&user_id=${encodeURIComponent(
      allocation.guard_member_id
    )}&alloc_id=${encodeURIComponent(allocation.allocation_id)}`;

    window.open(url, "_blank");
  };

  const openEditModal = (allocation) => {
    setEditForm({
      allocation_id: allocation.allocation_id || "",
      route_id: allocation.route_id || routeId,
      guard_member_id: allocation.guard_member_id || "",
      shift_label: allocation.shift_label || "",
      start_time: toDateTimeLocal(allocation.start_time),
      end_time: toDateTimeLocal(allocation.end_time),
    });

    setEditModalOpen(true);
  };

  const closeEditModal = () => {
    setEditModalOpen(false);

    setEditForm({
      allocation_id: "",
      route_id: "",
      guard_member_id: "",
      shift_label: "",
      start_time: "",
      end_time: "",
    });
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;

    setEditForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const saveEditAllocation = async () => {
    if (
      !editForm.allocation_id ||
      !editForm.guard_member_id ||
      !editForm.shift_label.trim() ||
      !editForm.start_time ||
      !editForm.end_time
    ) {
      toast.error("Please complete all fields.");
      return;
    }

    try {
      setSaving(true);

      const token = localStorage.getItem("token") || localStorage.getItem("authToken");

      const res = await fetch(
        `${API_BASE_URL}/sentinel_allocate/${editForm.allocation_id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            route_id: routeId,
            guard_member_id: editForm.guard_member_id,
            shift_label: editForm.shift_label.trim(),
            start_time: editForm.start_time,
            end_time: editForm.end_time,
          }),
        }
      );

      const data = await res.json();

      if (!res.ok || data.success === false) {
        throw new Error(data.message || "Failed to update allocation");
      }

      toast.success(data.message || "Allocation updated successfully.");
      closeEditModal();
      await fetchPageData(dashboardData, token, page, search);
    } catch (error) {
      console.error("Update allocation error:", error);
      toast.error(error.message || "Unexpected error updating allocation.");
    } finally {
      setSaving(false);
    }
  };

  const deleteAllocation = async (allocationId) => {
    if (!window.confirm("Delete this allocation? This cannot be undone.")) return;

    try {
      setDeletingId(allocationId);

      const token = localStorage.getItem("token") || localStorage.getItem("authToken");

      const res = await fetch(
        `${API_BASE_URL}/sentinel_allocate/${allocationId}`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const data = await res.json();

      if (!res.ok || data.success === false) {
        throw new Error(data.message || "Failed to delete allocation");
      }

      toast.success(data.message || "Allocation deleted successfully.");
      await fetchPageData(dashboardData, token, page, search);
    } catch (error) {
      console.error("Delete allocation error:", error);
      toast.error(error.message || "Unexpected error deleting allocation.");
    } finally {
      setDeletingId(null);
    }
  };

  const routePage = useMemo(
    () =>
      dashboardData.user_map === "google"
        ? "/sentinel_add_route"
        : "/sentinel_add_route",
    [dashboardData.user_map]
  );

  const paginationItems = useMemo(() => {
    const items = [];
    const start = Math.max(1, page - 2);
    const end = Math.min(totalPages, page + 2);

    if (start > 1) {
      items.push(1);
      if (start > 2) items.push("...");
    }

    for (let i = start; i <= end; i += 1) items.push(i);

    if (end < totalPages) {
      if (end < totalPages - 1) items.push("...");
      items.push(totalPages);
    }

    return items;
  }, [page, totalPages]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--page-bg)] flex flex-col items-center justify-center gap-3">
        <div className="w-9 h-9 border-3 border-[var(--accent-faint)] border-t-[var(--accent)] rounded-full animate-spin" />
        <p className="text-sm text-[var(--text-muted)] font-['Inter',system-ui,sans-serif]">
          Loading sentinel route allocations...
        </p>
      </div>
    );
  }

  return (
    <Layout
      dashboardData={dashboardData}
      routePage={routePage}
      activePage="sentinel-routes"
      activeMenu="sentinel"
    >
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 5000,
          style: {
            background: "var(--card-bg)",
            color: "var(--text-primary)",
            border: "1px solid var(--card-border)",
            borderRadius: "12px",
            padding: "16px 20px",
            fontSize: "13px",
            fontWeight: "600",
            boxShadow: "0 8px 32px rgba(0,0,0,0.3)",
          },
          success: {
            iconTheme: {
              primary: "#22c55e",
              secondary: "white",
            },
          },
          error: {
            iconTheme: {
              primary: "#ef4444",
              secondary: "white",
            },
          },
        }}
      />

      <div className="font-['Inter',system-ui,sans-serif] text-[var(--text-primary)]">
        <h1 className="text-[22px] font-extrabold text-[var(--text-primary)] tracking-tight m-0">
          Sentinel Route Allocations
        </h1>
        <p className="text-[13px] text-[var(--text-muted)] m-0 mb-[22px]">
          Route: <strong>{formatValue(routeName, `Sentinel Route #${routeId}`)}</strong> • ID: {routeId}
        </p>

        {/* Top Card */}
        <div className="relative overflow-hidden bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl px-7 py-6 shadow-[0_8px_32px_rgba(0,0,0,0.3)] mb-[18px]">
          <div className="absolute top-0 left-0 right-0 h-[3px] bg-[var(--accent)]" />
          <div className="flex items-center justify-between gap-3.5 flex-wrap">
            <div>
              <h2 className="text-[18px] font-extrabold text-[var(--text-primary)] m-0">
                Allocation List
              </h2>
              <p className="text-[13px] text-[var(--text-muted)] m-0 mt-1">
                Manage sentinel route guard allocations, timings, tracking map, edit and delete actions
              </p>
            </div>
            <button
              type="button"
              onClick={() => navigate("/sentinel_route_list")}
              className="border-0 rounded-xl px-4 py-2.5 text-[13px] font-extrabold cursor-pointer transition-transform duration-150 hover:opacity-95 hover:-translate-y-0.5"
              style={{
                background: "var(--btn-bg)",
                color: "var(--btn-text)",
                boxShadow: "0 4px 18px var(--accent-glow)",
              }}
            >
              Back to Sentinel Routes
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-[18px]">
          <div className="relative overflow-hidden rounded-xl p-4 bg-[var(--btn-bg)] border border-[var(--surface-border)] shadow-[0_4px_18px_var(--accent-glow)]">
            <p className="text-[10px] font-extrabold uppercase tracking-wider text-[var(--btn-text)] opacity-75 m-0 mb-1.5">
              Total Allocations
            </p>
            <p className="text-[30px] font-extrabold leading-none m-0 text-[var(--btn-text)]">
              {totalRecords}
            </p>
          </div>

          <div className="relative overflow-hidden rounded-xl p-4 bg-[var(--surface)] border border-[var(--surface-border)]">
            <p className="text-[10px] font-extrabold uppercase tracking-wider text-[var(--text-faint)] m-0 mb-1.5">
              Current Page
            </p>
            <p className="text-[30px] font-extrabold leading-none m-0 text-[var(--accent)]">
              {page} / {totalPages}
            </p>
          </div>

          <div className="relative overflow-hidden rounded-xl p-4 bg-[var(--surface)] border border-[var(--surface-border)]">
            <p className="text-[10px] font-extrabold uppercase tracking-wider text-[var(--text-faint)] m-0 mb-1.5">
              Per Page
            </p>
            <p className="text-[30px] font-extrabold leading-none m-0 text-[var(--accent)]">
              {recordsPerPage}
            </p>
          </div>

          <div className="relative overflow-hidden rounded-xl p-4 bg-[var(--surface)] border border-[var(--surface-border)]">
            <p className="text-[10px] font-extrabold uppercase tracking-wider text-[var(--text-faint)] m-0 mb-1.5">
              Status
            </p>
            <p className="text-[30px] font-extrabold leading-none m-0 text-[var(--accent)]">
              {allocations.length > 0 ? "Active" : "Empty"}
            </p>
          </div>
        </div>

        {/* Filter Card */}
        <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl px-7 py-6 shadow-[0_8px_32px_rgba(0,0,0,0.3)] mb-[18px]">
          <form onSubmit={handleSearchSubmit} className="flex gap-3 flex-wrap">
            <input
              type="text"
              placeholder="Search guard / shift..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="flex-1 min-w-[260px] rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] outline-none px-3.5 py-3 text-[13px] transition-colors duration-150 focus:border-[var(--accent)] focus:shadow-[0_0_0_3px_var(--accent-glow)] placeholder:text-[var(--text-faint)]"
            />

            <button
              type="submit"
              className="border-0 rounded-xl px-4 py-2.5 text-[13px] font-extrabold cursor-pointer transition-transform duration-150 hover:opacity-95 hover:-translate-y-0.5"
              style={{
                background: "var(--btn-bg)",
                color: "var(--btn-text)",
                boxShadow: "0 4px 18px var(--accent-glow)",
              }}
            >
              Search
            </button>

            {search ? (
              <button
                type="button"
                onClick={clearSearch}
                className="border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] rounded-xl px-4 py-2.5 text-[13px] font-extrabold cursor-pointer transition-colors duration-150 hover:bg-[var(--accent-faint)] hover:border-[var(--accent-border)]"
              >
                Clear
              </button>
            ) : null}
          </form>
        </div>

        {/* Allocations Table */}
        <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl overflow-hidden shadow-[0_8px_32px_rgba(0,0,0,0.3)]">
          <div className="px-5 py-[18px] border-b border-[var(--divider)] flex items-center justify-between gap-3 flex-wrap">
            <p className="text-[12px] font-extrabold uppercase tracking-wider text-[var(--text-sub)] m-0">
              Allocations Database
            </p>
            <span className="text-[12px] text-[var(--text-muted)]">
              {totalRecords} entries found
            </span>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full min-w-[900px] border-collapse">
              <thead>
                <tr>
                  {["#", "Guard", "Shift", "Start", "End", "Actions"].map((head) => (
                    <th
                      key={head}
                      className="bg-[var(--surface)] text-[var(--text-sub)] text-left text-[11px] font-extrabold uppercase tracking-wider px-[18px] py-3.5 border-b border-[var(--divider)]"
                    >
                      {head}
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody>
                {allocations.length > 0 ? (
                  allocations.map((row, index) => (
                    <tr key={row.allocation_id} className="border-b border-[var(--divider)] hover:bg-[var(--accent-faint)] transition-colors">
                      <td className="px-[18px] py-[15px] text-[13px] align-middle">
                        <span className="text-[var(--accent)] font-extrabold">
                          {(page - 1) * recordsPerPage + index + 1}
                        </span>
                      </td>

                      <td className="px-[18px] py-[15px] text-[13px] align-middle">
                        <div className="flex items-center gap-2.5">
                          <div className="w-[38px] h-[38px] rounded-xl bg-[var(--btn-bg)] text-[var(--btn-text)] flex items-center justify-center text-[14px] font-extrabold flex-shrink-0 shadow-[0_3px_12px_var(--accent-glow)]">
                            {String(row.guard_label || row.guard_name || row.username || row.fld_username || "U").charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <p className="text-[13px] font-extrabold text-[var(--text-primary)] m-0">
                              {formatValue(
                                row.guard_label ||
                                  row.guard_name ||
                                  row.username ||
                                  row.fld_username,
                                row.guard_member_id
                              )}
                            </p>
                            <p className="text-[12px] text-[var(--text-muted)] m-0">
                              ID: {row.guard_member_id}
                            </p>
                          </div>
                        </div>
                      </td>

                      <td className="px-[18px] py-[15px] text-[13px] align-middle">
                        <span className="inline-flex items-center px-3 py-1.5 rounded-full text-[11.5px] font-extrabold bg-[var(--accent-faint)] border border-[var(--accent-border)] text-[var(--accent)]">
                          {formatValue(row.shift_label)}
                        </span>
                      </td>

                      <td className="px-[18px] py-[15px] text-[13px] text-[var(--text-primary)] align-middle">
                        {formatDateTime(row.start_time, "Not specified")}
                      </td>

                      <td className="px-[18px] py-[15px] text-[13px] text-[var(--text-primary)] align-middle">
                        {formatDateTime(row.end_time, "Not specified")}
                      </td>

                      <td className="px-[18px] py-[15px] text-[13px] align-middle">
                        <div className="flex gap-2 flex-wrap">
                          <button
                            type="button"
                            title="View Sentinel Route on Map"
                            onClick={() => openRouteMap(row)}
                            className="border-0 rounded-xl px-3 py-2 text-[11px] font-extrabold cursor-pointer transition-colors duration-150 hover:opacity-90"
                            style={{
                              background: "#22c55e",
                              color: "white",
                            }}
                          >
                            Map
                          </button>

                          <button
                            type="button"
                            title="Edit Allocation"
                            onClick={() => openEditModal(row)}
                            className="border-0 rounded-xl px-3 py-2 text-[11px] font-extrabold cursor-pointer transition-colors duration-150 hover:opacity-90"
                            style={{
                              background: "#f59e0b",
                              color: "white",
                            }}
                          >
                            Edit
                          </button>

                          <button
                            type="button"
                            title="Delete Allocation"
                            onClick={() => deleteAllocation(row.allocation_id)}
                            className="border-0 rounded-xl px-3 py-2 text-[11px] font-extrabold cursor-pointer transition-colors duration-150 hover:opacity-90 disabled:opacity-60 disabled:cursor-not-allowed"
                            style={{
                              background: "var(--red)",
                              color: "white",
                            }}
                            disabled={deletingId === row.allocation_id}
                          >
                            {deletingId === row.allocation_id ? "..." : "Delete"}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={6} className="text-center py-8 text-[var(--text-muted)]">
                      No sentinel allocation data found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {totalPages > 1 ? (
            <div className="px-5 py-[15px] border-t border-[var(--divider)] flex items-center justify-between gap-3 flex-wrap">
              <div className="text-[12px] text-[var(--text-muted)]">
                Showing {(page - 1) * recordsPerPage + 1} to{" "}
                {Math.min(page * recordsPerPage, totalRecords)} of {totalRecords} entries
              </div>

              <div className="flex gap-1.5 flex-wrap">
                {page > 1 ? (
                  <button
                    type="button"
                    onClick={() => handlePageChange(page - 1)}
                    className="border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] rounded-xl px-3 py-2 text-[12px] font-extrabold cursor-pointer"
                  >
                    Previous
                  </button>
                ) : null}

                {paginationItems.map((item, index) =>
                  item === "..." ? (
                    <span key={`dots-${index}`} className="text-[var(--text-muted)] text-[16px] font-extrabold px-1 self-center">
                      …
                    </span>
                  ) : (
                    <button
                      key={item}
                      type="button"
                      onClick={() => handlePageChange(item)}
                      className={`border rounded-xl px-3 py-2 text-[12px] font-extrabold cursor-pointer ${
                        item === page
                          ? "bg-[var(--btn-bg)] text-[var(--btn-text)] border-[var(--accent-border)]"
                          : "border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)]"
                      }`}
                    >
                      {item}
                    </button>
                  )
                )}

                {page < totalPages ? (
                  <button
                    type="button"
                    onClick={() => handlePageChange(page + 1)}
                    className="border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] rounded-xl px-3 py-2 text-[12px] font-extrabold cursor-pointer"
                  >
                    Next
                  </button>
                ) : null}
              </div>
            </div>
          ) : null}
        </div>
      </div>

      {/* Edit Modal */}
      {editModalOpen ? (
        <div className="fixed inset-0 bg-black/55 backdrop-blur-sm flex items-center justify-center p-4 z-[2500]" onClick={closeEditModal}>
          <div className="w-full max-w-[720px] bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl overflow-hidden shadow-2xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center px-6 py-4 border-b border-[var(--divider)]">
              <h3 className="m-0 text-[22px] font-extrabold text-[var(--text-primary)]">
                Edit Sentinel Allocation
              </h3>
              <button
                type="button"
                onClick={closeEditModal}
                className="border-0 bg-transparent text-3xl leading-none cursor-pointer text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors"
              >
                ×
              </button>
            </div>

            <div className="p-6 bg-[var(--surface)]">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-[12px] font-extrabold uppercase tracking-wide text-[var(--text-sub)] mb-1.5">
                    Guard <span className="text-[var(--red)] ml-0.5">*</span>
                  </label>
                  <select
                    name="guard_member_id"
                    value={editForm.guard_member_id}
                    onChange={handleEditChange}
                    required
                    className="w-full rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] outline-none px-3.5 py-3 text-[13px] transition-colors duration-150 focus:border-[var(--accent)] focus:shadow-[0_0_0_3px_var(--accent-glow)]"
                  >
                    <option value="">
                      {guards.length ? "-- Select Guard --" : "Loading guards..."}
                    </option>
                    {guards.map((guard) => {
                      const memberId =
                        guard.member_id ||
                        guard.fld_memberId ||
                        guard.fld_memberid ||
                        guard.guard_member_id ||
                        "";

                      const username =
                        guard.username || guard.fld_username || guard.name || "";

                      return (
                        <option key={memberId} value={memberId}>
                          {username} ({memberId})
                        </option>
                      );
                    })}
                  </select>
                  <p className="mt-1 text-[11px] text-[var(--text-muted)]">
                    Pick a guard from active users.
                  </p>
                </div>

                <div className="md:col-span-2">
                  <label className="block text-[12px] font-extrabold uppercase tracking-wide text-[var(--text-sub)] mb-1.5">
                    Shift Label <span className="text-[var(--red)] ml-0.5">*</span>
                  </label>
                  <input
                    type="text"
                    name="shift_label"
                    value={editForm.shift_label}
                    onChange={handleEditChange}
                    placeholder="Morning / Evening / Night / Custom"
                    required
                    className="w-full rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] outline-none px-3.5 py-3 text-[13px] transition-colors duration-150 focus:border-[var(--accent)] focus:shadow-[0_0_0_3px_var(--accent-glow)] placeholder:text-[var(--text-faint)]"
                  />
                </div>

                <div>
                  <label className="block text-[12px] font-extrabold uppercase tracking-wide text-[var(--text-sub)] mb-1.5">
                    Start Date & Time <span className="text-[var(--red)] ml-0.5">*</span>
                  </label>
                  <input
                    type="datetime-local"
                    name="start_time"
                    value={editForm.start_time}
                    onChange={handleEditChange}
                    required
                    className="w-full rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] outline-none px-3.5 py-3 text-[13px] transition-colors duration-150 focus:border-[var(--accent)] focus:shadow-[0_0_0_3px_var(--accent-glow)]"
                  />
                </div>

                <div>
                  <label className="block text-[12px] font-extrabold uppercase tracking-wide text-[var(--text-sub)] mb-1.5">
                    End Date & Time <span className="text-[var(--red)] ml-0.5">*</span>
                  </label>
                  <input
                    type="datetime-local"
                    name="end_time"
                    value={editForm.end_time}
                    onChange={handleEditChange}
                    required
                    className="w-full rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] outline-none px-3.5 py-3 text-[13px] transition-colors duration-150 focus:border-[var(--accent)] focus:shadow-[0_0_0_3px_var(--accent-glow)]"
                  />
                </div>
              </div>
            </div>

            <div className="px-6 py-4 bg-[var(--card-bg)] border-t border-[var(--divider)] flex justify-end gap-3 flex-wrap">
              <button
                type="button"
                onClick={closeEditModal}
                className="border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] rounded-xl px-5 py-2.5 text-[13px] font-extrabold cursor-pointer transition-transform duration-150 hover:opacity-90 hover:-translate-y-0.5"
              >
                Close
              </button>

              <button
                type="button"
                onClick={saveEditAllocation}
                className="border-0 rounded-xl px-5 py-2.5 text-[13px] font-extrabold cursor-pointer transition-transform duration-150 hover:opacity-95 hover:-translate-y-0.5 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:translate-y-0"
                style={{
                  background: "var(--btn-bg)",
                  color: "var(--btn-text)",
                  boxShadow: "0 4px 18px var(--accent-glow)",
                }}
                disabled={saving}
              >
                {saving ? "Saving..." : "Save Changes"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </Layout>
  );
}